import {
    buildBreadcrumbManager,
    buildFacet,
    buildFieldSortCriterion,
    buildQuerySummary,
    buildRelevanceSortCriterion,
    buildResultList,
    buildSearchBox,
    buildSort,
    loadAdvancedSearchQueryActions,
    loadFacetSetActions,
    loadPaginationActions,
    loadQueryActions,
    loadSearchActions,
    loadSearchAnalyticsActions,
    loadSortCriteriaActions,
    SortOrder,
} from '@coveo/headless';
import { useGeolocationApi, useGeolocationState } from 'contexts/GeolocationContext';
import { getHeadlessEngine } from 'libs/coveo/getHeadlessEngine';
import React, { useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useSearchParams } from '@/hooks/useSearchParams';
import { trackInteraction } from '@/utils/analytics';

import { useLocationSetStateContext } from './LocationStateContext';

const defaultValue = {
    coveo: {
        engine: null,
        controller: {
            searchBox: null,
            querySummary: null,
            resultList: null,
            sort: null,
        },
        action: {
            updateQuery: null,
            executeSearch: null,
        },
    },
    sortCriteria: null,
    handleSubmit: () => {},
    handleSingleFilter: () => {},
    handleMultipleFilters: () => {},
    handleFilterResetAll: () => {},
    handleShowResultList: () => {},
    handleShowMaps: () => {},
    triggerExecuteSearch: () => {},
    noResultsComponent: null,
    errorComponent: null,
};

export const sortCriteria = [
    ['Relevancy', buildRelevanceSortCriterion()],
    ['Distance', buildFieldSortCriterion('mydistanceinmiles', SortOrder.Ascending)],
    // TODO - need to be added on COVEO
    // ['Wait time', buildFieldSortCriterion('wait_time', SortOrder.Ascending)],
];

export const mapUrlParamsToQuery = {
    term: (v) => v,
    'open-now': (v) => {
        if (v !== 'true') {
            return '';
        }

        const now = new Date(new Date().toLocaleString('en', { timeZone: 'America/New_York' }));
        const weekday = [
            'sunday',
            'monday',
            'tuesday',
            'wednesday',
            'thursday',
            'friday',
            'saturday',
        ][now.getDay()];
        const nowInSeconds = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
        // const nowInSeconds = 23400; // 06:00:00
        // const nowInSeconds = 36000; // 10:00:00
        // const nowInSeconds = 43200; // 12:00:00
        // const nowInSeconds = 45000; // 12:30:00
        // const nowInSeconds = 46800; // 13:00:00
        // const nowInSeconds = 54000; // 15:00:00

        return [
            '(',
            `@locworkinghourssearch_${weekday}_closed = "False"`,
            `AND (@locworkinghourssearch_${weekday}_start <= ${nowInSeconds} AND @locworkinghourssearch_${weekday}_end >= ${nowInSeconds})`,
            `AND NOT (@locworkinghourssearch_${weekday}_breakstart < ${nowInSeconds} AND @locworkinghourssearch_${weekday}_breakend > ${nowInSeconds})`,
            ')',
        ].join(' ');
    },
};

export const mapUrlParamsToQueryDistance = {
    distance: (v) => (v && v > 0 ? `@mydistanceinmiles <= ${v}` : ''),
};

export const mapUrlParamsToQueryZipCity = {
    'zip-code': (v) => (v ? `@locationzip == ${v}` : ''),
    city: (v) => {
        const arr = v?.split(',');
        return v ? `@locationcity == "${arr[0]}" AND @locationstate == ${arr[arr.length - 1]}` : '';
    },
};

export const mapUrlParamsToSort = {
    sort: (v) => sortCriteria.find(([name]) => name === v)?.[1] ?? sortCriteria[0][1],
};

export const urlParamFilterNames = ['type', 'specialty', 'condition', 'treatment', 'acc-ins'];

export const urlParamViewMode = ['view-mode'];

export const allUrlParamNames = [
    ...Object.keys(mapUrlParamsToQuery),
    ...Object.keys(mapUrlParamsToQueryDistance),
    ...Object.keys(mapUrlParamsToQueryZipCity),
    ...Object.keys(mapUrlParamsToSort),
    ...urlParamFilterNames,
    ...urlParamViewMode,
];

const removeUrlParams = (searchParams, exclude = []) => {
    const newSearchParams = new URLSearchParams(searchParams);

    const excludesEntries = [];
    for (const name of exclude) {
        const value = newSearchParams.getAll(name).filter(Boolean);
        if (!value || value.length === 0) {
            continue;
        }
        excludesEntries.push([name, value]);
    }

    for (const name of allUrlParamNames) {
        newSearchParams.delete(name);
    }

    for (const [name, value] of excludesEntries) {
        newSearchParams.append(name, value);
    }

    return newSearchParams;
};

let geolocation = null;

const LocationSearchContext = React.createContext(defaultValue);
const LocationSearchZipCodeErrorContext = React.createContext(false);

export const LocationSearchProvider = (props) => {
    const { id, children, coveo, locationCategory, noResultsComponent, errorComponent } = props;

    const [zipCodeServerError, setZipCodeServerError] = React.useState(false);

    const coveoAnalyticsRef = useRef({ event: '', payload: {}, shouldReset: true });

    const history = useHistory();
    const searchParams = useSearchParams();

    const setState = useLocationSetStateContext();

    const { zipCode, city } = useGeolocationState() ?? {};

    const { setGeolocation } = useGeolocationApi();
    const engine = React.useMemo(
        () =>
            getHeadlessEngine(`location-search-${locationCategory || 'default'}-${id}`, {
                ...coveo,
                preprocessRequest: (request, _clientOrigin, metadata) => {
                    if (!metadata || metadata.method !== 'search' || geolocation == null) {
                        return request;
                    }

                    const body = JSON.parse(request.body);

                    body.queryFunctions = [
                        {
                            fieldName: '@mydistance',
                            function: `dist(@latitude, @longitude, ${geolocation.lat ?? 0}, ${
                                geolocation.lng ?? 0
                            })`,
                        },
                        {
                            fieldName: '@mydistanceinmiles',
                            function: '@mydistance*0.000621371',
                        },
                    ];

                    request.body = JSON.stringify(body);

                    return request;
                },
                analytics: { analyticsMode: 'legacy', trackingId: 'location-search' },
            }),
        [coveo, locationCategory, id],
    );

    const value = React.useMemo(() => {
        const AdvancedSearchQueryActions = loadAdvancedSearchQueryActions(engine);
        if (locationCategory) {
            engine.dispatch(
                AdvancedSearchQueryActions.updateAdvancedSearchQueries({
                    cq: `@loctype=${locationCategory}`,
                }),
            );
        }

        const QueryActionCreators = loadQueryActions(engine);
        const SortCriteriaActions = loadSortCriteriaActions(engine);
        const FacetSetActions = loadFacetSetActions(engine);
        const SearchAnalyticsActionCreators = loadSearchAnalyticsActions(engine);
        const SearchActionCreators = loadSearchActions(engine);
        const PaginationActions = loadPaginationActions(engine);

        const controllerSearchBox = buildSearchBox(engine, {
            options: { id: 'location-search-searchbox', numberOfSuggestions: 10 },
        });
        const controllerQuerySummary = buildQuerySummary(engine);
        const controllerResultList = buildResultList(engine, {
            options: {
                fieldsToInclude: [
                    'entitytype',
                    'loctype',
                    'locconditions',

                    // Distance
                    'mydistance',
                    'mydistanceinmiles',
                    'latitude',
                    'longitude',

                    // Open now
                    // 'locworkinghourssearch',

                    // Result List
                    'locname',
                    'locvariant',
                    'locimgurl',
                    'locparent',
                    'locbuildingname',
                    'locaddrline1',
                    'locaddrline2',
                    'locaddrline3',
                    'locationstate',
                    'locationzip',
                    'locmapurl',
                    'locphnum1',
                    'locworkinghours',
                    'locadjustedhours',
                    'locopen24hours',
                    'loctemporarilyclosed',
                    'locoverridemessage',
                    'locwaittime',
                    'locepicdepartmentid',
                    'locroot',
                ],
            },
        });

        if (searchParams.get('view-mode') === 'maps') {
            engine.dispatch(PaginationActions.registerNumberOfResults(999999));
        }

        const initialCriterion = sortCriteria[0][1];
        const controllerSort = buildSort(engine, {
            initialState: { criterion: initialCriterion },
        });

        const factoryFacet = (facetId, field) => {
            return buildFacet(engine, {
                options: {
                    facetId,
                    field,
                    numberOfValues: 999999,
                    resultsMustMatch: 'allValues',
                    // resultsMustMatch: 'atLeastOneValue',
                    sortCriteria: 'alphanumeric',
                },
            });
        };

        const controllerFacetType = factoryFacet('type', 'loctype');
        const controllerFacetSpecialty = factoryFacet('specialty', 'locspecialties');
        const controllerFacetCondition = factoryFacet('condition', 'loccondition');
        const controllerFacetTreatment = factoryFacet('treatment', 'locservices');
        // TODO - need to be added on COVEO
        const controllerFacetAcceptedInsurance = factoryFacet('acc-ins', 'locacceptedinsurance');

        engine.dispatch(FacetSetActions.updateFacetAutoSelection({ allow: true }));

        const controllerBreadcrumbManager = buildBreadcrumbManager(engine);

        const executeSearch = (event, payload = {}) => {
            const {
                logInterfaceLoad,
                logSearchboxSubmit,
                logResultsSort,
                logQuerySuggestionClick,
                logFacetSelect,
                logFacetDeselect,
                logFacetBreadcrumb,
                logClearBreadcrumbs,
                logStaticFilterSelect,
                logStaticFilterDeselect,
                logStaticFilterClearAll,
            } = SearchAnalyticsActionCreators;
            switch (event) {
                case 'resultsSort':
                    return SearchActionCreators.executeSearch(logResultsSort());
                case 'searchboxSubmit':
                    return SearchActionCreators.executeSearch(logSearchboxSubmit());
                case 'querySuggestionClick':
                    return SearchActionCreators.executeSearch(logQuerySuggestionClick(payload)); // LogQuerySuggestionClickActionCreatorPayload {id: string, suggestion: string}
                case 'facetSelect':
                    return SearchActionCreators.executeSearch(logFacetSelect(payload)); // LogFacetSelectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetDeselect':
                    return SearchActionCreators.executeSearch(logFacetDeselect(payload)); // LogFacetDeselectActionCreatorPayload {facetId: string, facetValue: string}
                case 'facetBreadcrumb':
                    return SearchActionCreators.executeSearch(logFacetBreadcrumb(payload)); // LogFacetBreadcrumbActionCreatorPayload {facetId: string, facetValue: string}
                case 'clearBreadcrumbs':
                    return SearchActionCreators.executeSearch(logClearBreadcrumbs());
                case 'staticFilterSelect':
                    return SearchActionCreators.executeSearch(logStaticFilterSelect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterDeselect':
                    return SearchActionCreators.executeSearch(logStaticFilterDeselect(payload)); // LogStaticFilterToggleValueActionCreatorPayload {staticFilterId: string, staticFilterValue: StaticFilterValueMetadata}
                case 'staticFilterClearAll':
                    return SearchActionCreators.executeSearch(logStaticFilterClearAll(payload)); // LogStaticFilterClearAllActionCreatorPayload {staticFilterId: string}
                default:
                    return SearchActionCreators.executeSearch(logInterfaceLoad());
            }
        };

        const triggerExecuteSearch = async (urlParams) => {
            setState((prevState) => {
                if (prevState === 'default') {
                    coveoAnalyticsRef.current.shouldReset = false;
                }
                return 'results';
            });

            const { sort, 'zip-code': zipCode, city, 'view-mode': viewMode } = urlParams;

            setZipCodeServerError(false);

            const newGeolocation = await setGeolocation({ zipCode, city });
            geolocation = newGeolocation;

            if ((!!zipCode || !!city) && newGeolocation == null) {
                setZipCodeServerError(true);
                return;
            }

            const queryArray = [];
            for (const name in mapUrlParamsToQuery) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQuery, name)) {
                    const fn = mapUrlParamsToQuery[name];
                    queryArray.push(fn(urlParams[name]));
                }
            }
            const query = queryArray.filter(Boolean).join(' AND ');

            const zipCityQueryArray = [];
            for (const name in mapUrlParamsToQueryZipCity) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQueryZipCity, name)) {
                    const fn = mapUrlParamsToQueryZipCity[name];
                    zipCityQueryArray.push(fn(urlParams[name]));
                }
            }
            const zipCityQuery = zipCityQueryArray.filter(Boolean).join(' AND ');

            const distanceQueryArray = [];
            for (const name in mapUrlParamsToQueryDistance) {
                if (Object.hasOwnProperty.call(mapUrlParamsToQueryDistance, name)) {
                    const fn = mapUrlParamsToQueryDistance[name];
                    distanceQueryArray.push(fn(urlParams[name]));
                }
            }

            if (distanceQueryArray.filter(Boolean).length > 0) {
                distanceQueryArray.push(zipCityQuery);
            }

            const distanceQuery = distanceQueryArray
                .filter(Boolean)
                .map((q, index) => (index > 0 ? `(${q})` : q))
                .join(' OR ');

            engine.dispatch(SortCriteriaActions.updateSortCriterion(mapUrlParamsToSort.sort(sort)));

            for (const name of urlParamFilterNames) {
                engine.dispatch(FacetSetActions.deselectAllFacetValues(name));

                const values = urlParams[name];
                const filteredValues = values?.filter(Boolean);

                if (!filteredValues || filteredValues.length === 0) {
                    continue;
                }

                filteredValues.forEach((v) => {
                    const [value, numberOfResults] = v.split(';');

                    engine.dispatch(
                        FacetSetActions.toggleSelectFacetValue({
                            facetId: name,
                            selection: {
                                value,
                                numberOfResults: parseInt(numberOfResults, 10),
                                state: 'selected',
                            },
                        }),
                    );
                });
            }

            engine.dispatch(
                QueryActionCreators.updateQuery({
                    enableQuerySyntax: true,
                    q: `${query}`,
                }),
            );

            engine.dispatch(
                AdvancedSearchQueryActions.updateAdvancedSearchQueries({
                    aq: `${distanceQuery || ''}`,
                }),
            );

            engine.dispatch(PaginationActions.updateNumberOfResults(viewMode ? 999999 : 10));
            engine.dispatch(
                executeSearch(
                    coveoAnalyticsRef?.current?.event,
                    coveoAnalyticsRef?.current?.payload,
                ),
            );
        };

        const setCoveoAnalytics = (event = '', payload = {}, shouldReset = true) => {
            coveoAnalyticsRef.current.event = event;
            coveoAnalyticsRef.current.payload = payload;
            coveoAnalyticsRef.current.shouldReset = shouldReset;
        };

        const handleSubmit = ({ term, zipCityValue, viewMode, analyticsCallback }) => {
            const newSearchParams = removeUrlParams(new URLSearchParams(history.location.search));

            const values = [
                { name: 'term', value: term },
                { name: 'zip-code', value: !isNaN(zipCityValue) ? zipCityValue : null },
                { name: 'city', value: isNaN(zipCityValue) ? zipCityValue : null },
                { name: 'view-mode', value: viewMode },
                ...(zipCityValue ? [{ name: 'distance', value: '' }] : []),
                ...(zipCityValue ? [{ name: 'sort', value: 'Distance' }] : []),
            ];

            values.forEach(({ name, value }) => {
                if (value) {
                    newSearchParams.set(name, value);
                } else {
                    newSearchParams.delete(name);
                }
            });

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            const currentPath = window.location.pathname;
            const currentUrl = `${currentPath}${history.location.search}`;

            if (currentUrl === newUrl) {
                return false;
            }

            if (!analyticsCallback) {
                setCoveoAnalytics('searchboxSubmit', {}, false);
            } else {
                analyticsCallback();
            }

            history.push(newUrl);
        };

        const handleSingleFilter = (partial, analytics) => {
            const newSearchParams = new URLSearchParams(history.location.search);

            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];

                    if (value) {
                        newSearchParams.set(name, value);
                    } else {
                        newSearchParams.delete(name);
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'Filter Sort Panel',
                    interactionType: 'filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }

            history.push(newUrl);
        };

        const handleMultipleFilters = (partial, analytics) => {
            const newSearchParams = new URLSearchParams(history.location.search);

            for (const name in partial) {
                if (Object.hasOwnProperty.call(partial, name)) {
                    const value = partial[name];

                    if (!newSearchParams.has(name, value)) {
                        newSearchParams.append(name, value);
                    } else {
                        newSearchParams.delete(name, value);
                    }
                }
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            // analytics
            if (analytics) {
                trackInteraction({
                    componentName: 'Filter Sort Panel',
                    interactionType: 'filter',
                    selector: 'filter',
                    allFilters: newSearchParams,
                    ...analytics,
                });
            }
            history.push(newUrl);
        };

        const handleFilterResetAll = () => {
            const newSearchParams = removeUrlParams(new URLSearchParams(history.location.search), [
                'term',
                'zip-code',
                'city',
                'view-mode',
            ]);

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            history.push(newUrl);
        };

        const handleShowResultList = () => {
            const searchParams = new URLSearchParams(history.location.search);

            searchParams.delete('view-mode');

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(newUrl);
        };

        const handleShowMaps = () => {
            const searchParams = new URLSearchParams(history.location.search);

            searchParams.set('view-mode', 'maps');

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(newUrl);
        };

        const handleOpenNow = (openNow) => {
            const searchParams = new URLSearchParams(history.location.search);

            if (openNow) {
                searchParams.set('open-now', 'true');
            } else {
                searchParams.delete('open-now');
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${searchParams.toString()}`;

            history.push(newUrl);
        };

        return {
            coveo: {
                engine,
                controller: {
                    searchBox: controllerSearchBox,
                    querySummary: controllerQuerySummary,
                    resultList: controllerResultList,
                    sort: controllerSort,
                    facet: {
                        type: controllerFacetType,
                        specialty: controllerFacetSpecialty,
                        condition: controllerFacetCondition,
                        treatment: controllerFacetTreatment,
                        acceptedInsurance: controllerFacetAcceptedInsurance,
                    },
                    breadcrumbManager: controllerBreadcrumbManager,
                },
                action: {
                    updateQuery: QueryActionCreators.updateQuery,
                    executeSearch,
                },
            },
            sortCriteria,
            handleSubmit,
            handleSingleFilter,
            handleMultipleFilters,
            handleFilterResetAll,
            handleShowResultList,
            handleShowMaps,
            handleOpenNow,
            triggerExecuteSearch,
            noResultsComponent,
            setCoveoAnalytics,
            errorComponent,
        };
    }, [
        locationCategory,
        engine,
        searchParams,
        noResultsComponent,
        errorComponent,
        setState,
        setGeolocation,
        history,
    ]);

    React.useEffect(() => {
        if (locationCategory) {
            return;
        }

        if (
            !searchParams.has('term') &&
            !searchParams.has('zip-code') &&
            !searchParams.has('city')
        ) {
            return;
        }

        const urlParamQueryNames = [
            ...Object.keys(mapUrlParamsToQuery),
            ...Object.keys(mapUrlParamsToQueryDistance),
            ...Object.keys(mapUrlParamsToQueryZipCity),
            ...Object.keys(mapUrlParamsToSort),
            ...urlParamViewMode,
        ];

        const values = {};
        for (const name of urlParamQueryNames) {
            values[name] = searchParams.get(name);
        }

        for (const name of urlParamFilterNames) {
            values[name] = searchParams.getAll(name);
        }

        value.triggerExecuteSearch(values);
    }, [locationCategory, searchParams, setState, value]);

    const firstRenderRef = React.useRef(true);
    React.useEffect(() => {
        if (!locationCategory) {
            return;
        }

        const urlParamQueryNames = [
            ...Object.keys(mapUrlParamsToQuery),
            ...Object.keys(mapUrlParamsToSort),
            ...Object.keys(mapUrlParamsToQueryDistance),
            ...Object.keys(mapUrlParamsToQueryZipCity),
            ...urlParamViewMode,
        ];

        const values = {};
        for (const name of urlParamQueryNames) {
            values[name] = searchParams.get(name);
        }

        for (const name of urlParamFilterNames) {
            values[name] = searchParams.getAll(name);
        }

        if (
            ((zipCode != null && zipCode !== values['zip-code']) ||
                (city != null && city !== values['city'])) &&
            firstRenderRef.current
        ) {
            firstRenderRef.current = false;

            const newSearchParams = new URLSearchParams(history.location.search);
            if (zipCode != null) {
                newSearchParams.set('zip-code', zipCode);
            } else if (city != null) {
                newSearchParams.set('city', city);
            }

            const path = window.location.pathname;
            const newUrl = `${path}?${newSearchParams.toString()}`;

            history.replace(newUrl);

            return;
        }

        firstRenderRef.current = false;

        delete values['term'];

        value.triggerExecuteSearch(values);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationCategory, searchParams, setState, value]);

    return (
        <LocationSearchContext.Provider value={value}>
            <LocationSearchZipCodeErrorContext.Provider value={zipCodeServerError}>
                {children}
            </LocationSearchZipCodeErrorContext.Provider>
        </LocationSearchContext.Provider>
    );
};

export const useLocationSearchContext = () => {
    const context = React.useContext(LocationSearchContext);

    if (context == null) {
        throw new Error(`'useLocationSearchContext' needs to be within 'LocationSearchContext'`);
    }

    return context;
};

export const useLocationSearchZipCodeErrorContext = () => {
    const context = React.useContext(LocationSearchZipCodeErrorContext);

    if (context == null) {
        throw new Error(
            `'useLocationSearchZipCodeErrorContext' needs to be within 'LocationSearchZipCodeErrorContext'`,
        );
    }

    return context;
};
